import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import {
  Form,
  FormLayout,
  TextField,
  Select,
  Button,
  InlineStack,
  BlockStack,
  Text,
  Card,
  Badge,
  Spinner,
  Banner,
  Checkbox,
  Autocomplete,
} from "@shopify/polaris";
import { AppDispatch, RootState } from "configureStore";
import {
  createOrderReservation,
  updateOrderReservation,
} from "../reservations.slice";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";
import {
  ReservationStatus,
  CreateReservationInput,
  fetchReservationById,
} from "services/reservations.service";
import { fetchById as fetchOrderById } from "services/orders.service";
import { getReservationStatusBadgeTone } from "./ReservationsSection";
import { fetch as fetchProducts } from "containers/products/products.service";

interface ReservationFormProps {
  orderId: string;
  reservationId?: string;
  mode: "create" | "edit" | "view";
  preSelectedProductId?: string;
}

interface OrderItem {
  id: string;
  product?: string;
  productTitle?: string;
  sku?: string;
  supplier_sku?: string;
  quantity: number;
  unitPrice?: number;
  description?: string;
}

interface LocalReservationItem {
  id?: string;
  product: any;
  productName?: string;
  sku?: string;
  quantity: number;
  orderLineType?: string | null;
  orderLineId?: string | null;
  notes?: string | null;
}

const STATUS_OPTIONS: { label: string; value: ReservationStatus }[] = [
  { label: "Draft", value: "draft" },
  { label: "Confirmed", value: "confirmed" },
  { label: "Cancelled", value: "cancelled" },
];

export default function ReservationForm({
  orderId,
  reservationId,
  mode,
  preSelectedProductId,
}: ReservationFormProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { companyId } = useParams<{ companyId: string }>();

  const { selectedReservation } = useSelector(
    (state: RootState) => state.orderReservationsReducer
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stockWarnings, setStockWarnings] = useState<any[]>([]);
  const [hasCreatedReservation, setHasCreatedReservation] = useState(false);

  // Form state
  const [status, setStatus] = useState<ReservationStatus>("confirmed");
  const [notes, setNotes] = useState("");
  const [customerFillsDelivery, setCustomerFillsDelivery] = useState(false);
  const [items, setItems] = useState<LocalReservationItem[]>([]);
  const [remainingByProduct, setRemainingByProduct] = useState<Record<string, number>>({});

  // Order items (available to add)
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [selectedOrderItems, setSelectedOrderItems] = useState<Set<string>>(new Set());
  const [itemQuantities, setItemQuantities] = useState<Record<string, number>>({});
  const autoSelectDoneRef = useRef(false);

  // Product search (for adding any product)
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [productQuantity, setProductQuantity] = useState<string>("1");
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [productSearchQuery, setProductSearchQuery] = useState<string>("");
  const [productSearchResults, setProductSearchResults] = useState<any[]>([]);

  // Load order items for create/edit mode
  useEffect(() => {
    const loadOrderItems = async () => {
      if (!companyId || !orderId) return;
      
      try {
        const { orderEntity } = await fetchOrderById(orderId, companyId);
        if (orderEntity?.items) {
          setOrderItems(orderEntity.items);
          const reservations = orderEntity?.reservations || [];
          const reservedByProduct: Record<string, number> = {};
          reservations.forEach((reservation: any) => {
            if (reservation.status !== "confirmed") return;
            (reservation.items || []).forEach((resItem: any) => {
              const productRef = resItem.product;
              const productId =
                typeof productRef === "object" && productRef !== null
                  ? productRef.id
                  : productRef;
              if (!productId) return;
              reservedByProduct[productId] =
                (reservedByProduct[productId] || 0) + (resItem.quantity || 0);
            });
          });
          const quantities: Record<string, number> = {};
          const remainingMap: Record<string, number> = {};
          orderEntity.items.forEach((item: OrderItem) => {
            const productId =
              typeof item.product === "object" && item.product !== null
                ? (item.product as any).id
                : item.product;
            const orderedQty = item.quantity || 0;
            const remainingQty = Math.max(
              0,
              orderedQty - (reservedByProduct[productId] || 0)
            );
            remainingMap[productId] = remainingQty;
            quantities[item.id] = Math.min(orderedQty, remainingQty);
          });
          setItemQuantities(quantities);
          setRemainingByProduct(remainingMap);
        }
      } catch (err) {
        console.error("Failed to load order items:", err);
      }
    };

    if (mode === "create" || mode === "edit") {
      loadOrderItems();
    }
  }, [companyId, orderId, mode]);

  useEffect(() => {
    autoSelectDoneRef.current = false;
  }, [preSelectedProductId]);

  useEffect(() => {
    if (mode !== "create" || !preSelectedProductId) return;
    if (!orderItems.length) return;
    if (autoSelectDoneRef.current) return;

    const matchingItemIds = orderItems
      .filter((item) => {
        const productId =
          typeof item.product === "object" && item.product !== null
            ? (item.product as any).id
            : item.product;
        return productId === preSelectedProductId;
      })
      .map((item) => item.id);

    if (matchingItemIds.length > 0) {
      setSelectedOrderItems(new Set(matchingItemIds));
    }
    autoSelectDoneRef.current = true;
  }, [mode, preSelectedProductId, orderItems]);

  // Load initial products for manual product selection
  useEffect(() => {
    const loadProducts = async () => {
      if ((mode === "create" || mode === "edit") && companyId) {
        try {
          const { productsCollection } = await fetchProducts(companyId);
          setAllProducts(productsCollection || []);
          setProductSearchResults(productsCollection || []);
        } catch (err) {
          console.error("Failed to load products:", err);
        }
      }
    };
    loadProducts();
  }, [mode, companyId]);

  // Debounced product search
  const handleProductSearch = useCallback(
    debounce(async (query: string) => {
      if (!companyId) return;
      try {
        const filter = query ? { textSearch: query } : {};
        const { productsCollection } = await fetchProducts(companyId, filter);
        setProductSearchResults(productsCollection || []);
      } catch (err) {
        console.error("Failed to search products:", err);
      }
    }, 300),
    [companyId]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      handleProductSearch.cancel();
    };
  }, [handleProductSearch]);

  const onProductSearchChange = useCallback(
    (value: string) => {
      setProductSearchQuery(value);
      if (value === "") {
        setProductSearchResults(allProducts);
      } else {
        handleProductSearch(value);
      }
    },
    [handleProductSearch, allProducts]
  );

  // Load reservation data for edit/view modes
  useEffect(() => {
    const loadReservation = async () => {
      if ((mode === "edit" || mode === "view") && reservationId && companyId) {
        setIsLoading(true);
        try {
          const { reservation } = await fetchReservationById(companyId, reservationId);
          setStatus(reservation.status);
          setNotes(reservation.notes || "");
          setCustomerFillsDelivery(!!reservation.customerFillsDelivery);
          setItems((reservation.items || []).map((item: any) => ({
            ...item,
          })) as LocalReservationItem[]);
        } catch (err: any) {
          setError(err.message || t("reservations_error_loading"));
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (selectedReservation && selectedReservation.id === reservationId) {
      setStatus(selectedReservation.status);
      setNotes(selectedReservation.notes || "");
      setCustomerFillsDelivery(!!selectedReservation.customerFillsDelivery);
      setItems((selectedReservation.items || []).map((item: any) => ({
        ...item,
      })) as LocalReservationItem[]);
    } else {
      loadReservation();
    }
  }, [mode, reservationId, companyId, selectedReservation, t]);

  const handleClose = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleToggleOrderItem = (itemId: string) => {
    const newSelected = new Set(selectedOrderItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedOrderItems(newSelected);
  };

  const handleQuantityChange = (itemId: string, value: string) => {
    const qty = parseFloat(value) || 0;
    const orderItem = orderItems.find((item) => item.id === itemId);
    const productId =
      typeof orderItem?.product === "object" && orderItem?.product !== null
        ? (orderItem?.product as any).id
        : orderItem?.product;
    const maxQty = productId ? remainingByProduct[productId] ?? qty : qty;
    const clampedQty = Math.min(Math.max(0, qty), maxQty);
    setItemQuantities((prev) => ({ ...prev, [itemId]: clampedQty }));
  };

  const handleAddSelectedItems = () => {
    const newItems: LocalReservationItem[] = [];
    
    selectedOrderItems.forEach((itemId) => {
      const orderItem = orderItems.find((oi) => oi.id === itemId);
      if (orderItem) {
        const orderItemProductId = orderItem.product || itemId;
        const maxRemaining = remainingByProduct[orderItemProductId] ?? (orderItem.quantity || 0);
        
        // Check if product already in items
        const existingByProduct = items.findIndex((i) => {
          const existingProductId = typeof i.product === "object" ? i.product.id : i.product;
          return existingProductId === orderItemProductId;
        });
        
        if (existingByProduct === -1 && maxRemaining > 0) {
          newItems.push({
            product: orderItemProductId,
            productName: orderItem.productTitle || "",
            sku: orderItem.sku || "",
            quantity: Math.min(itemQuantities[itemId] || maxRemaining, maxRemaining),
            orderLineType: "orderItem",
            orderLineId: itemId,
            notes: "",
          });
        }
      }
    });

    if (newItems.length > 0) {
      setItems([...items, ...newItems]);
      setSelectedOrderItems(new Set());
    }
  };

  const handleAddProductFromSearch = () => {
    if (!selectedProductId) return;

    const product = productSearchResults.find((p) => p.id === selectedProductId) 
      || allProducts.find((p) => p.id === selectedProductId);
    if (!product) return;

    const maxRemaining = remainingByProduct[selectedProductId];
    if (maxRemaining !== undefined && maxRemaining <= 0) {
      setError(t("reservations_no_available_quantity"));
      return;
    }

    // Check if already in items (same product)
    const existingIndex = items.findIndex(
      (i) => {
        const productId = typeof i.product === "object" ? i.product.id : i.product;
        return productId === selectedProductId;
      }
    );

    if (existingIndex === -1) {
      const newItem: LocalReservationItem = {
        product: selectedProductId,
        productName: product.title || "",
        sku: product.sku || product.supplier_sku || "",
        quantity:
          maxRemaining !== undefined
            ? Math.min(parseFloat(productQuantity) || 1, maxRemaining)
            : parseFloat(productQuantity) || 1,
        orderLineType: null,
        orderLineId: null,
        notes: "",
      };
      setItems([...items, newItem]);
    }

    setSelectedProductId("");
    setProductQuantity("1");
    setProductSearchQuery("");
    setProductSearchResults(allProducts);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleItemQuantityChange = (index: number, value: string) => {
    const qty = parseFloat(value) || 0;
    const newItems = [...items];
    const productId =
      typeof newItems[index].product === "object" && newItems[index].product !== null
        ? newItems[index].product.id
        : newItems[index].product;
    const maxQty = productId ? remainingByProduct[productId] ?? qty : qty;
    newItems[index] = { ...newItems[index], quantity: Math.min(Math.max(0, qty), maxQty) };
    setItems(newItems);
  };

  const handleItemNotesChange = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], notes: value };
    setItems(newItems);
  };

  const handleSubmit = async () => {
    if (!companyId) return;

    if (items.length === 0) {
      setError(t("reservations_error_no_items"));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let result: { reservation: any; warnings?: any[] } | undefined;
      
      if (mode === "create") {
        const createData: CreateReservationInput = {
          status,
          notes: notes || undefined,
          customerFillsDelivery,
          items: items.map((item) => ({
            product: typeof item.product === "object" ? item.product.id : item.product,
            quantity: item.quantity,
            orderLineType: item.orderLineType || null,
            orderLineId: item.orderLineId || null,
            notes: item.notes || null,
          })),
        };
        result = await dispatch(
          createOrderReservation(companyId, orderId, createData)
        );
      } else if (mode === "edit" && reservationId) {
        result = await dispatch(
          updateOrderReservation(companyId, reservationId, {
            status,
            notes: notes || undefined,
            customerFillsDelivery,
            items: items.map((item) => ({
              id: item.id,
              product: typeof item.product === "object" ? item.product.id : item.product,
              quantity: item.quantity,
              orderLineType: item.orderLineType || null,
              orderLineId: item.orderLineId || null,
              notes: item.notes || null,
            })),
          })
        );
      }

      // Refresh order details to update reservations list
      dispatch(fetchOrderDetails({ companyId, orderId }));
      
      // Check for stock warnings
      if (result?.warnings && result.warnings.length > 0) {
        setStockWarnings(result.warnings);
        setHasCreatedReservation(true);
        // Don't close modal - show warnings to user
      } else {
        handleClose();
      }
    } catch (err: any) {
      setError(err.message || t("reservations_error_saving"));
    } finally {
      setIsSaving(false);
    }
  };

  const isReadOnly = mode === "view";
  const canAddItems = !isReadOnly && (mode === "create" || status === "draft");

  // Filter out order items already in reservation
  const availableOrderItems = orderItems.filter((orderItem) => {
    const orderItemProductId = orderItem.product || orderItem.id;
    const maxRemaining = remainingByProduct[orderItemProductId] ?? (orderItem.quantity || 0);

    if (preSelectedProductId && orderItemProductId !== preSelectedProductId) {
      return false;
    }
    
    const existsByProduct = items.some((ri) => {
      const riProductId = typeof ri.product === "object" ? ri.product.id : ri.product;
      return riProductId === orderItemProductId;
    });
    
    return !existsByProduct && maxRemaining > 0;
  });

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        {stockWarnings.length > 0 && (
          <Banner
            tone="warning"
            title={t("reservations_stock_warnings_title")}
            onDismiss={() => setStockWarnings([])}
          >
            <BlockStack gap="200">
              {stockWarnings.map((warning, idx) => (
                <Text as="p" key={idx}>
                  {t("reservations_stock_warning_message", {
                    requested: warning.requested,
                    available: warning.available,
                  })}
                </Text>
              ))}
              <Button onClick={handleClose}>{t("close")}</Button>
            </BlockStack>
          </Banner>
        )}

        {/* Status */}
        {(mode === "create" || mode === "edit") && (
          <Select
            label={t("status")}
            options={STATUS_OPTIONS.map((opt) => ({
              ...opt,
              label: t(`reservations_status_${opt.value}`),
            }))}
            value={status}
            onChange={(val) => setStatus(val as ReservationStatus)}
            disabled={isReadOnly}
          />
        )}

        {/* Notes */}
        <TextField
          label={t("notes")}
          value={notes}
          onChange={setNotes}
          multiline={3}
          autoComplete="off"
          disabled={isReadOnly}
        />

        {/* Customer fills delivery */}
        {(mode === "create" || mode === "edit") && (
          <Checkbox
            label={t("reservations_customer_fills_delivery")}
            helpText={t("reservations_customer_fills_delivery_help")}
            checked={customerFillsDelivery}
            onChange={setCustomerFillsDelivery}
            disabled={isReadOnly}
          />
        )}
        {mode === "view" && customerFillsDelivery && (
          <Badge tone="info">{t("reservations_customer_fills_delivery")}</Badge>
        )}

        {/* Current Items */}
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("reservations_items")} ({items.length})
            </Text>

            {items.length === 0 ? (
              <Text as="p" tone="subdued">
                {t("reservations_no_items_yet")}
              </Text>
            ) : (
              <BlockStack gap="200">
                {items.map((item, index) => {
                  const productName =
                    item.productName ||
                    (typeof item.product === "object" && item.product !== null
                      ? item.product.title
                      : item.product) ||
                    "-";
                  const sku =
                    item.sku ||
                    (typeof item.product === "object" && item.product !== null
                      ? item.product.sku
                      : null) ||
                    "-";
                  return (
                    <Card key={index}>
                      <InlineStack align="space-between" blockAlign="center" gap="200">
                        <BlockStack gap="100">
                          <Text as="span" variant="bodyMd">
                            {productName}
                          </Text>
                          <Text as="span" variant="bodySm" tone="subdued">
                            SKU: {sku}
                          </Text>
                        </BlockStack>
                        <InlineStack gap="200" blockAlign="center">
                          <div style={{ width: "80px" }}>
                            <TextField
                              label=""
                              labelHidden
                              value={String(item.quantity)}
                              onChange={(val) => handleItemQuantityChange(index, val)}
                              autoComplete="off"
                              disabled={isReadOnly}
                            />
                          </div>
                          <div style={{ minWidth: "160px" }}>
                            <TextField
                              label={t("notes")}
                              labelHidden
                              value={item.notes || ""}
                              placeholder={t("notes_placeholder")}
                              onChange={(val) => handleItemNotesChange(index, val)}
                              autoComplete="off"
                              disabled={isReadOnly}
                            />
                          </div>
                          {!isReadOnly && (
                            <Button
                              onClick={() => handleRemoveItem(index)}
                              tone="critical"
                              variant="plain"
                            >
                              {t("remove")}
                            </Button>
                          )}
                        </InlineStack>
                      </InlineStack>
                    </Card>
                  );
                })}
              </BlockStack>
            )}
          </BlockStack>
        </Card>

        {/* Add items from order */}
        {canAddItems && availableOrderItems.length > 0 && (
          <>
            <Card>
              <BlockStack gap="300">
                <Text as="h3" variant="headingSm">
                  {t("reservations_add_from_order")}
                </Text>

                <BlockStack gap="200">
                  {availableOrderItems.map((orderItem) => (
                    <Card key={orderItem.id}>
                      <InlineStack align="space-between" blockAlign="center">
                        <Checkbox
                          label={
                            <BlockStack gap="100">
                              <Text as="span" variant="bodyMd">
                                {orderItem.productTitle || "-"}
                              </Text>
                              <Text as="span" variant="bodySm" tone="subdued">
                                SKU: {orderItem.sku || "-"} | {t("ordered")}: {orderItem.quantity}
                              </Text>
                            </BlockStack>
                          }
                          checked={selectedOrderItems.has(orderItem.id)}
                          onChange={() => handleToggleOrderItem(orderItem.id)}
                        />
                        <div style={{ width: "80px" }}>
                          <TextField
                            label=""
                            labelHidden
                            value={String(itemQuantities[orderItem.id] || orderItem.quantity || 1)}
                            onChange={(val) => handleQuantityChange(orderItem.id, val)}
                            autoComplete="off"
                          />
                        </div>
                      </InlineStack>
                    </Card>
                  ))}
                </BlockStack>

                {selectedOrderItems.size > 0 && (
                  <Button onClick={handleAddSelectedItems}>
                    {t("reservations_add_selected")} ({selectedOrderItems.size})
                  </Button>
                )}
              </BlockStack>
            </Card>
          </>
        )}

        {/* Add any product */}
        {canAddItems && (
          <>
            <Card>
              <BlockStack gap="300">
                <Text as="h3" variant="headingSm">
                  {t("reservations_add_any_product")}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {t("reservations_add_any_product_description")}
                </Text>

                <InlineStack gap="200" blockAlign="end">
                  {/* On a phone the search takes a row of its own; quantity and Add go under it. */}
                  <div className="min-w-0 basis-full sm:flex-1">
                    <Autocomplete
                      options={productSearchResults.map((p) => ({
                        value: p.id,
                        label: `${p.title} (${p.sku || p.supplier_sku || "-"})`,
                      }))}
                      selected={selectedProductId ? [selectedProductId] : []}
                      onSelect={(selected) => {
                        const id = selected[0];
                        setSelectedProductId(id || "");
                      }}
                      textField={
                        <Autocomplete.TextField
                          label={t("search_products")}
                          value={productSearchQuery}
                          onChange={onProductSearchChange}
                          autoComplete="off"
                          placeholder={t("search_products")}
                        />
                      }
                    />
                  </div>
                  <div style={{ width: "80px" }}>
                    <TextField
                      label={t("quantity")}
                      value={productQuantity}
                      onChange={setProductQuantity}
                      autoComplete="off"
                    />
                  </div>
                  <Button
                    onClick={handleAddProductFromSearch}
                    disabled={!selectedProductId}
                  >
                    {t("add")}
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
          </>
        )}

        {/* Actions */}
        <InlineStack gap="200" align="end">
          <Button onClick={handleClose}>{t("cancel")}</Button>
          {!isReadOnly && (
            <Button
              variant="primary"
              submit
              loading={isSaving}
              disabled={hasCreatedReservation}
            >
              {mode === "create" ? t("create") : t("save")}
            </Button>
          )}
        </InlineStack>
      </BlockStack>
    </Form>
  );
}
